import Typography from '@mui/material/Typography';
import React, {
  ReactNode,
  Reducer,
  useMemo,
  useReducer,
  useState,
} from 'react';
import Box from '@mui/material/Box';
import {
  colors,
  MIRA_CUSTOMER_BY_ID_QUERY_KEY,
  OFFER_SHEET_QUERY_KEY,
  TABLE_SIZE_OPTIONS,
} from '../../../../../shared/constants';
import { getOfferSheets } from '../../../services/offerSheets';
import {
  TableColumn,
  TablePaginationOptions,
  TableSortOptions,
  TableSortOrder,
} from '../../../../../shared/types';
import {
  DatabaseOfferWithApprovalRequest,
  GetOffersTableFilterOptions,
  OfferState,
} from '../../../../../shared/types/offers';
import { DataTable } from '../../../components/Common/DataTable/DataTable';
import { useDataTableFiltering } from '../../../hooks/useDataTableFiltering';
import { produce } from 'immer';
import { OffersTableCellComponent } from './OffersTableCellComponent';
import { OffersTableRowActions } from './OffersTableRowActions';
import { OffersTableColumnFilter } from './OfferTableColumnFilter';
import { OffersTableRowDetails } from './OffersTableRowDetails';
import { useQuery } from '@tanstack/react-query';
import { getMiraCustomerByIds } from '../../../services/customers';
import GenericContainer from '../../../GenericContainer';
import { OffersSummaryTable } from './OffersSummaryTable';
import TopLevelFilters from '../../PricingSheets/PricingSheetsTable/TopLevelFilters';
import { addDays } from 'date-fns';
import { useApi } from '../../../hooks/useApi';

export type RenderComponentPropsParent<
  T extends (...args: any) => ReactNode = (...args: any) => ReactNode,
> = (
  renderProps: Parameters<T>[0],
  parentProps: {
    refetch: () => void;
    loading: boolean;
  },
) => ReturnType<T>;

export type RenderComponentProps<
  T extends (...args: any) => ReactNode = (...args: any) => ReactNode,
> = (renderProps: Parameters<T>[0]) => ReturnType<T>;

type TableState = {
  filterState: GetOffersTableFilterOptions;
  paginationState: TablePaginationOptions;
  sortState: TableSortOptions<DatabaseOfferWithApprovalRequest>;
};
type OffersTableToggleExpiredOptions = {
  toggleExpired: ExpiredOptions;
};
type OffersTableToggleStateOptions = {
  checked: Boolean;
  toggleState: OfferState;
};
type OffersTableToggleArchivedOptions = {
  toggleArchived: 'all' | 'notArchived' | 'archived';
};
type TableAction =
  | {
      type: 'SET_FILTER';
      payload: GetOffersTableFilterOptions;
    }
  | {
      type: 'SET_PAGINATION';
      payload: TablePaginationOptions;
    }
  | {
      type: 'TOGGLE_STATE';
      payload: OffersTableToggleStateOptions;
    }
  | {
      type: 'TOGGLE_ARCHIVED_FILTER';
      payload: OffersTableToggleArchivedOptions;
    }
  | {
      type: 'TOGGLE_EXPIRED_FILTER';
      payload: OffersTableToggleExpiredOptions;
    }
  | {
      type: 'SET_SORT';
      payload: TableSortOptions<DatabaseOfferWithApprovalRequest>;
    };

const DEFAULT_FILTER: GetOffersTableFilterOptions = {
  byUser: true,
  archived: false,
  removed: false,
  validTo: undefined,
  includeStates: [
    OfferState.Closed,
    OfferState.Open,
    OfferState.Draft,
    OfferState.InternalApproval,
    OfferState.InternalRejection,
    OfferState.ReadyForSending,
    OfferState.Expired,
    OfferState.Sent,
    OfferState.Rejected,
    OfferState.Approved,
  ],
};

const DEFAULT_PAGINATION: TablePaginationOptions = {
  limit: TABLE_SIZE_OPTIONS[3],
  offset: 0,
};

const DEFAULT_SORT: TableSortOptions<DatabaseOfferWithApprovalRequest> =
  {
    direction: TableSortOrder.Desc,
    orderBy: 'updatedAt',
  };

const INITIAL_STATE: TableState = {
  filterState: {
    ...DEFAULT_FILTER,
    archived: false,
    removed: false,
  },
  paginationState: DEFAULT_PAGINATION,
  sortState: DEFAULT_SORT,
};

const TABLE_COLUMNS: TableColumn<DatabaseOfferWithApprovalRequest>[] =
  [
    {
      key: 'id',
      label: 'Tarjousnro',
      width: 80,
    },
    {
      key: 'name',
      label: 'Nimi',
    },
    {
      key: 'customerName',
      label: 'Asiakas',
    },
    {
      key: 'worksite',
      label: 'Työmaa',
    },
    {
      key: 'userEmail',
      label: 'Tekijä',
    },
    {
      key: 'responsibleWarehouseName',
      label: 'Vastuuvarasto',
    },
    {
      key: 'state',
      label: 'Tila',
    },
    {
      key: 'createdAt',
      label: 'Luotu',
    },
    {
      key: 'offerValidityEnd',
      label: 'Voimassa asti',
    },
    {
      key: 'totalCoefficient',
      label: 'Hintakerroin',
    },
    {
      key: 'totalPrice',
      label: 'Yht.',
    },
  ];

const reducer: Reducer<TableState, TableAction> = produce(
  (state, { type, payload }) => {
    switch (type) {
      case 'SET_FILTER':
        state.filterState = payload;
        break;
      case 'SET_PAGINATION':
        state.paginationState = payload;
        break;
      case 'TOGGLE_EXPIRED_FILTER': {
        const { toggleExpired } = payload;

        switch (toggleExpired) {
          case 'all': {
            state.filterState.validTo = undefined;
            break;
          }
          case 'notExpired': {
            state.filterState.validTo = `${new Date()}|gte`;
            break;
          }
          case 'expiring': {
            const queryString = `${new Date()}~${addDays(
              new Date(),
              3,
            )}|between`;
            state.filterState.validTo = queryString;
            break;
          }
          case 'expired': {
            state.filterState.validTo = `${new Date()}|lte`;
            break;
          }
        }
        // state.paginationState.offset = 0;
        break;
      }
      case 'TOGGLE_ARCHIVED_FILTER': {
        const { toggleArchived } = payload;

        switch (toggleArchived) {
          case 'all': {
            delete state.filterState.archived;
            break;
          }
          case 'notArchived': {
            state.filterState.archived = false;
            break;
          }
          case 'archived': {
            state.filterState.archived = true;
            break;
          }
        }
        // state.paginationState.offset = 0;
        break;
      }
      case 'TOGGLE_STATE': {
        const { toggleState, checked } = payload;

        let includeStates = state.filterState.includeStates ?? [];

        if (checked) {
          includeStates = Array.from(
            new Set([...includeStates, toggleState]),
          );
        } else {
          includeStates = includeStates.filter(
            (s) => s !== toggleState,
          );
        }
        state.filterState.includeStates = includeStates;
        // state.paginationState.offset = 0;
        break;
      }
      case 'SET_SORT':
        state.sortState = payload;
        break;
      default:
        return state;
    }
  },
);
type ExpiredOptions = 'all' | 'notExpired' | 'expiring' | 'expired';

const formatCount = (value: (number | null | undefined)[]) => {
  const sum = value.reduce<number>((acc, cur) => {
    if (cur) {
      return acc + cur;
    }
    return acc;
  }, 0);
  return ` (${sum})`;
};

export type ReturnBodyCounts = {
  totalCountClosed: number;
  totalCountOpen: number;
  totalCount: number;
  totalArchived: number;
  totalCountApproved: number;
  totalCountAwaitingOrder: number;
  totalCountDraft: number;
  totalCountExpired: number;
  totalCountInternalApproval: number;
  totalCountInternalRejection: number;
  totalCountOrdered: number;
  totalCountPartiallyOrdered: number;
  totalCountReadyForSending: number;
  totalCountRejected: number;
  totalCountRequiresChanges: number;
  totalCountSent: number;
};

type FilterQueryParam = { key: string; value: any };

const buildQuery = (queryParams: FilterQueryParam[]) => {
  const query = queryParams
    .filter((param) => param.value !== undefined)
    .map((param) => `${param.key}=${param.value}`)
    .join(',');
  return query ? '?filter=' + query : '';
};

export const OfferSheetsTable = () => {
  const [state, dispatch] = useReducer(reducer, {
    ...INITIAL_STATE,
  });
  const { filterState, paginationState, sortState } = state;
  const [expirationState, setExpirationState] =
    useState<ExpiredOptions>('all');

  const { query } = useDataTableFiltering<
    DatabaseOfferWithApprovalRequest,
    GetOffersTableFilterOptions
  >({
    queryOptions: {
      queryKey: [OFFER_SHEET_QUERY_KEY],
      queryFn: getOfferSheets,
    },
    filterOptions: filterState,
    paginationOptions: paginationState,
    sortOptions: sortState,
  });

  const {
    data: { count: itemCount = 0, items = [] } = {},
    isFetching,
    refetch,
  } = query;

  // fetch customer blocking info for each customer by Id
  const customerIds = items.map((item) => item.customerId).join(',');
  const { data: miraCustomers = [], isFetching: isFetchingBlocking } =
    useQuery({
      queryKey: [MIRA_CUSTOMER_BY_ID_QUERY_KEY, customerIds],
      enabled: itemCount > 0,
      refetchOnWindowFocus: 'always',
      queryFn: async () => getMiraCustomerByIds(customerIds),
      retry: false,
    });
  const [offerCountsByFilter] = useApi<ReturnBodyCounts>(
    `/offers/count${buildQuery([
      { key: 'byUser', value: filterState.byUser },
      { key: 'archived', value: filterState.archived },
      { key: 'validTo', value: filterState.validTo },
    ])}`,
  );
  const [offerCountsTotal] = useApi<ReturnBodyCounts>(
    `/offers/count${buildQuery([
      { key: 'byUser', value: true },
      { key: 'archived', value: false },
    ])}`,
  );
  const isStateChecked = (state: OfferState) =>
    filterState.includeStates?.includes(state);

  const setStateFilter = (
    checked: Boolean,
    toggleState: OfferState,
  ) => {
    dispatch({
      type: 'TOGGLE_STATE',
      payload: { toggleState, checked },
    });
  };

  const parentProps: Parameters<RenderComponentPropsParent>[1] =
    useMemo(
      () => ({
        refetch,

        loading: isFetching,
      }),
      [isFetching, refetch],
    );

  return (
    <GenericContainer borderColor={colors.ramiOrange}>
      <Box marginBottom={3}>
        <Typography variant={'h4'} color={'primary'}>
          Tarjoukset
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          justifyContent: 'space-between',
        }}
      >
        <TopLevelFilters
          topToggleFilters={[
            {
              key: 'onlyMine',
              label: 'Näytä vain omat',
              checked: filterState.byUser ?? false,
              onChange: (checked) => {
                dispatch({
                  type: 'SET_FILTER',
                  payload: {
                    ...filterState,
                    byUser: checked,
                  },
                });
              },
            },
          ]}
          onResetFilters={() => {
            setExpirationState('all');
            dispatch({
              type: 'SET_FILTER',
              payload: DEFAULT_FILTER,
            });
          }}
          bottomRadioFilters={[
            {
              key: 'expiration',
              selectedKey: expirationState,
              title: 'Voimassaolo',
              options: [
                {
                  key: 'all',
                  label: 'Kaikki',
                },
                {
                  key: 'notExpired',
                  label: 'Voimassa',
                },
                {
                  key: 'expiring',
                  label: 'Vanhentuvat',
                },
                {
                  key: 'expired',
                  label: 'Vanhentuneet',
                },
              ],
              onChange: (value: any) => {
                setExpirationState(value);
                dispatch({
                  type: 'TOGGLE_EXPIRED_FILTER',
                  payload: { toggleExpired: value },
                });
              },
            },
            {
              key: 'archive',
              selectedKey:
                filterState.archived === undefined
                  ? 'all'
                  : filterState?.archived === true
                    ? 'archived'
                    : 'notArchived',
              title: 'Arkisto',
              options: [
                {
                  key: 'all',
                  label: 'Kaikki',
                },
                {
                  key: 'notArchived',
                  label: 'Ei arkistoituja',
                },
                {
                  key: 'archived',
                  label: 'Vain arkistoidut',
                },
              ],
              onChange: (value: any) => {
                dispatch({
                  type: 'TOGGLE_ARCHIVED_FILTER',
                  payload: { toggleArchived: value },
                });
              },
            },
          ]}
          filters={[
            {
              label:
                'Näytä kaikki' +
                formatCount([
                  offerCountsByFilter?.totalCountOpen,
                  offerCountsByFilter?.totalCountDraft,
                  offerCountsByFilter?.totalCountInternalApproval,
                  offerCountsByFilter?.totalCountInternalRejection,
                  offerCountsByFilter?.totalCountReadyForSending,
                  offerCountsByFilter?.totalCountSent,
                  offerCountsByFilter?.totalCountExpired,
                  offerCountsByFilter?.totalCountApproved,
                  offerCountsByFilter?.totalCountRejected,
                ]),

              children: [
                {
                  label:
                    'Keskeneräiset' +
                    formatCount([
                      offerCountsByFilter?.totalCountOpen,
                      offerCountsByFilter?.totalCountDraft,
                      offerCountsByFilter?.totalCountInternalApproval,
                      offerCountsByFilter?.totalCountInternalRejection,
                      offerCountsByFilter?.totalCountReadyForSending,
                    ]),
                  children: [
                    {
                      label:
                        'Avoin' +
                        formatCount([
                          offerCountsByFilter?.totalCountOpen,
                        ]),
                      checked: isStateChecked(OfferState.Open),
                      onChecked: (checked) => {
                        setStateFilter(checked, OfferState.Open);
                      },
                    },
                    {
                      label:
                        'Luonnos' +
                        formatCount([
                          offerCountsByFilter?.totalCountDraft,
                        ]),
                      checked: isStateChecked(OfferState.Draft),
                      onChecked: (checked) => {
                        setStateFilter(checked, OfferState.Draft);
                      },
                    },
                    {
                      label:
                        'Sisäinen hyväksyntä' +
                        formatCount([
                          offerCountsByFilter?.totalCountInternalApproval,
                        ]),
                      checked: isStateChecked(
                        OfferState.InternalApproval,
                      ),
                      onChecked: (checked) =>
                        setStateFilter(
                          checked,
                          OfferState.InternalApproval,
                        ),
                    },
                    {
                      label:
                        'Palautettu' +
                        formatCount([
                          offerCountsByFilter?.totalCountInternalRejection,
                        ]),
                      checked: isStateChecked(
                        OfferState.InternalRejection,
                      ),
                      onChecked: (checked) =>
                        setStateFilter(
                          checked,
                          OfferState.InternalRejection,
                        ),
                    },
                    {
                      label:
                        'Valmis lähetettäväksi' +
                        formatCount([
                          offerCountsByFilter?.totalCountReadyForSending,
                        ]),
                      checked: isStateChecked(
                        OfferState.ReadyForSending,
                      ),
                      onChecked: (checked) =>
                        setStateFilter(
                          checked,
                          OfferState.ReadyForSending,
                        ),
                    },
                  ],
                },
                {
                  label:
                    'Lähetetty asiakkaalle' +
                    formatCount([
                      offerCountsByFilter?.totalCountSent,
                      offerCountsByFilter?.totalCountExpired,
                      offerCountsByFilter?.totalCountApproved,
                      offerCountsByFilter?.totalCountRejected,
                    ]),
                  children: [
                    {
                      label:
                        'Lähetetty' +
                        formatCount([
                          offerCountsByFilter?.totalCountSent,
                        ]),
                      checked: isStateChecked(OfferState.Sent),
                      onChecked: (checked) => {
                        setStateFilter(checked, OfferState.Sent);
                      },
                    },
                    {
                      label:
                        'Vanhentunut' +
                        formatCount([
                          offerCountsByFilter?.totalCountExpired,
                        ]),
                      checked: isStateChecked(OfferState.Expired),
                      onChecked: (checked) =>
                        setStateFilter(checked, OfferState.Expired),
                    },
                    {
                      label:
                        'Hyväksytty' +
                        formatCount([
                          offerCountsByFilter?.totalCountApproved,
                        ]),
                      checked: isStateChecked(OfferState.Approved),
                      onChecked: (checked) => {
                        setStateFilter(checked, OfferState.Approved);
                      },
                    },
                    {
                      label:
                        'Hylätty' +
                        formatCount([
                          offerCountsByFilter?.totalCountRejected,
                        ]),
                      checked: isStateChecked(OfferState.Rejected),
                      onChecked: (checked) => {
                        setStateFilter(checked, OfferState.Rejected);
                      },
                    },
                  ],
                },
                {
                  label:
                    'Suljettu' +
                    formatCount([
                      offerCountsByFilter?.totalCountClosed,
                    ]),
                  checked: isStateChecked(OfferState.Closed),
                  onChecked: (checked) =>
                    setStateFilter(checked, OfferState.Closed),
                },
              ],
            },
          ]}
        />
        <Box mt={4}>
          <OffersSummaryTable
            offerCounts={offerCountsTotal}
            onStateFilterClick={(states: OfferState[]) => {
              const updatedFilter = {
                ...filterState,
                byUser: true,
                includeStates: states,
                archived: false,
              };
              dispatch({
                type: 'SET_FILTER',
                payload: updatedFilter,
              });
              setExpirationState('all');
            }}
            onArchiveFilterClick={() => {
              const updatedFilter = {
                ...filterState,
                byUser: true,
                archived: true,
                includeStates: [
                  OfferState.Draft,
                  OfferState.InternalApproval,
                  OfferState.InternalRejection,
                  OfferState.ReadyForSending,
                  OfferState.Expired,
                  OfferState.Sent,
                  OfferState.Rejected,
                  OfferState.Approved,
                ],
              };
              setExpirationState('all');
              dispatch({
                type: 'SET_FILTER',
                payload: updatedFilter,
              });
            }}
          />
        </Box>
      </Box>
      {/* OFFERS TABLE */}
      <DataTable
        count={itemCount}
        filterOptions={filterState}
        labelNoResults={'Tarjouksia ei löytynyt!'}
        labelRowsPerPage={'Tarjouksia per sivu'}
        loading={isFetching}
        // miraCustomers={miraCustomers}
        items={items}
        paginationOptions={paginationState}
        renderCellComponent={(renderProps) =>
          OffersTableCellComponent(renderProps, parentProps)
        }
        renderRowActions={(renderProps) =>
          OffersTableRowActions(renderProps, parentProps)
        }
        renderColumnFilter={(renderProps) =>
          OffersTableColumnFilter(renderProps, parentProps)
        }
        renderRowDetails={(renderProps) =>
          OffersTableRowDetails(renderProps, parentProps)
        }
        setFilterOptions={(updatedFilter: any) => {
          const finalFilter =
            typeof updatedFilter === 'function'
              ? updatedFilter(filterState)
              : updatedFilter;

          dispatch({
            type: 'SET_FILTER',
            payload: finalFilter,
          });
        }}
        setPaginationOptions={(updatedPagination) => {
          dispatch({
            type: 'SET_PAGINATION',
            payload: updatedPagination,
          });
        }}
        setSortOptions={(updatedSort) => {
          dispatch({ type: 'SET_SORT', payload: updatedSort });
        }}
        sortOptions={sortState}
        tableColumns={TABLE_COLUMNS}
      />
    </GenericContainer>
  );
};
